import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Users,
  ExternalLink,
  AlertCircle,
  Search,
  Eye,
  Edit2,
  Send,
  TrendingUp,
  Instagram,
  Facebook,
  Youtube,
  Globe,
  UserCheck,
  UserX,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Loader2,
  FileText,
  CreditCard,
  BarChart3,
} from "lucide-react";
import { SiTiktok, SiPinterest } from "react-icons/si";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import AdminNavigation from "@/components/AdminNavigation";
import AdminFooter from "@/components/AdminFooter";

// Types
interface InfluencerApplication {
  id: number;
  userId: number;
  fullName: string;
  email: string;
  phoneNumber: string;
  location: {
    city: string;
    stateOrCountry: string;
  };
  bio: string;
  socialMedia: {
    instagram?: { handle?: string; followers?: number };
    facebook?: { url?: string; followers?: number };
    tiktok?: { username?: string; followers?: number };
    youtube?: { url?: string; subscribers?: number };
    pinterest?: { profile?: string; followers?: number };
    blog?: { url?: string; monthlyVisitors?: number };
    otherPlatforms?: string;
  };
  audienceDemographics: {
    ageRanges: string[];
    interests: string[];
    geographicReach: string[];
  };
  contentExperience: {
    focusAreas: string[];
    previousCollaborations: string;
    partnershipReason: string;
    portfolioLinks?: string[];
  };
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  rejectionReason?: string;
  commissionRate?: number;
  createdAt: string;
  updatedAt: string;
}

interface Influencer {
  id: number;
  email: string;
  nickname: string | null;
  stripeConnectAccountId: string | null;
  influencerData: {
    referralCode: string;
    commissionRate: number;
    isApproved: boolean;
    totalEarnings: number;
    totalReferrals: number;
    pendingEarnings: number;
    onboardingCompleted?: boolean;
    payoutsEnabled?: boolean;
    chargesEnabled?: boolean;
  };
  application?: InfluencerApplication;
  createdAt: string;
}

interface InfluencerPerformance {
  influencerId: number;
  dailyStats: Array<{
    date: string;
    conversions: number;
    revenue: number;
    commission: number;
  }>;
  monthlyStats: Array<{
    month: string;
    conversions: number;
    revenue: number;
    commission: number;
  }>;
  recentReferrals: Array<{
    id: number;
    orderId: number;
    orderAmount: number;
    commission: number;
    createdAt: string;
    customerEmail: string;
  }>;
  mediaDownloads: Array<{
    id: number;
    filename: string;
    downloadedAt: string;
  }>;
}

// Utility functions
const formatFollowers = (count: number | undefined): string => {
  if (!count) return "0";
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

const calculateTotalFollowers = (socialMedia: InfluencerApplication['socialMedia']): number => {
  let total = 0;
  if (socialMedia.instagram?.followers) total += socialMedia.instagram.followers;
  if (socialMedia.facebook?.followers) total += socialMedia.facebook.followers;
  if (socialMedia.tiktok?.followers) total += socialMedia.tiktok.followers;
  if (socialMedia.youtube?.subscribers) total += socialMedia.youtube.subscribers;
  if (socialMedia.pinterest?.followers) total += socialMedia.pinterest.followers;
  if (socialMedia.blog?.monthlyVisitors) total += socialMedia.blog.monthlyVisitors;
  return total;
};

export default function AdminInfluencers() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  // State management
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [followerRange, setFollowerRange] = useState({ min: "", max: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<InfluencerApplication | null>(null);
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [showPerformance, setShowPerformance] = useState(false);
  const [actionInfluencer, setActionInfluencer] = useState<{ 
    id: number; 
    action: 'approve' | 'reject' | 'suspend' | 'reactivate'; 
    commissionRate?: number;
    reason?: string;
  } | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [commissionRate, setCommissionRate] = useState(10);
  const itemsPerPage = 10;

  // Check admin role
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
      navigate('/home');
    }
  }, [user, navigate, toast]);

  // Fetch applications
  const { data: applications, isLoading: isLoadingApplications } = useQuery<InfluencerApplication[]>({
    queryKey: ['/api/admin/influencer-applications', { statusFilter, dateRange, followerRange }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (dateRange.from) params.append('fromDate', dateRange.from);
      if (dateRange.to) params.append('toDate', dateRange.to);
      if (followerRange.min) params.append('minFollowers', followerRange.min);
      if (followerRange.max) params.append('maxFollowers', followerRange.max);
      
      const response = await fetch(`/api/admin/influencer-applications?${params}`);
      if (!response.ok) throw new Error('Failed to fetch applications');
      return response.json();
    },
    enabled: user?.role === 'admin',
  });

  // Fetch approved influencers
  const { data: influencers, isLoading: isLoadingInfluencers } = useQuery<Influencer[]>({
    queryKey: ['/api/admin/influencers'],
    enabled: user?.role === 'admin',
  });

  // Fetch performance data
  const { data: performanceData, isLoading: isLoadingPerformance } = useQuery<InfluencerPerformance>({
    queryKey: ['/api/admin/influencer-performance', selectedInfluencer?.id],
    queryFn: async () => {
      const response = await fetch(`/api/influencer/dashboard`);
      if (!response.ok) throw new Error('Failed to fetch performance data');
      // Mock data for demonstration
      return {
        influencerId: selectedInfluencer?.id || 0,
        dailyStats: [],
        monthlyStats: [
          { month: 'Jan', conversions: 5, revenue: 500, commission: 50 },
          { month: 'Feb', conversions: 8, revenue: 800, commission: 80 },
          { month: 'Mar', conversions: 12, revenue: 1200, commission: 120 },
        ],
        recentReferrals: [
          { id: 1, orderId: 123, orderAmount: 99.99, commission: 9.99, createdAt: new Date().toISOString(), customerEmail: 'customer@example.com' }
        ],
        mediaDownloads: []
      };
    },
    enabled: !!selectedInfluencer && showPerformance,
  });

  // Review application mutation
  const reviewMutation = useMutation({
    mutationFn: async ({ id, approved, commissionRate, adminNotes, rejectionReason }: {
      id: number;
      approved: boolean;
      commissionRate?: number;
      adminNotes?: string;
      rejectionReason?: string;
    }) => {
      return apiRequest(`/api/admin/influencer-applications/${id}/review`, 'POST', {
        approved,
        commissionRate: commissionRate ? commissionRate / 100 : 0.1,
        adminNotes,
        rejectionReason,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/influencer-applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/influencers'] });
      toast({
        title: variables.approved ? "Application Approved" : "Application Rejected",
        description: `The application has been ${variables.approved ? 'approved' : 'rejected'}.`,
      });
      setSelectedApplication(null);
      setActionInfluencer(null);
      setAdminNotes("");
      setCommissionRate(10);
    },
    onError: (error: any) => {
      toast({
        title: "Review Failed",
        description: error?.message || "Failed to review application",
        variant: "destructive",
      });
    },
  });

  // Update commission mutation
  const updateCommissionMutation = useMutation({
    mutationFn: async ({ id, commissionRate }: { id: number; commissionRate: number }) => {
      return apiRequest(`/api/admin/influencers/${id}/commission`, 'POST', {
        commissionRate: commissionRate / 100,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/influencers'] });
      toast({
        title: "Commission Updated",
        description: "The commission rate has been updated successfully.",
      });
    },
  });

  // Payout mutation
  const payoutMutation = useMutation({
    mutationFn: async (influencerId: number) => {
      return apiRequest('/api/influencer/stripe-connect/payout', 'POST', { influencerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/influencers'] });
      toast({
        title: "Payout Initiated",
        description: "The payout has been initiated successfully.",
      });
    },
  });

  // Filter and sort data
  const filteredApplications = useMemo(() => {
    if (!applications) return [];
    
    let filtered = [...applications];
    
    // Filter by tab
    if (activeTab === 'pending') {
      filtered = filtered.filter(app => app.status === 'pending');
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(app => app.status === 'approved');
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(app => app.status === 'rejected');
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app =>
        app.fullName.toLowerCase().includes(query) ||
        app.email.toLowerCase().includes(query)
      );
    }
    
    // Sort
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = sortConfig.key === 'followers' 
          ? calculateTotalFollowers(a.socialMedia)
          : a[sortConfig.key as keyof InfluencerApplication];
        const bValue = sortConfig.key === 'followers'
          ? calculateTotalFollowers(b.socialMedia)
          : b[sortConfig.key as keyof InfluencerApplication];
        
        // Handle undefined values
        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [applications, activeTab, searchQuery, sortConfig]);

  // Paginate data
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredApplications.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredApplications, currentPage]);

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  // Handle sorting
  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (columnKey: string) => {
    if (sortConfig?.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="ml-2 h-4 w-4" />
      : <ChevronDown className="ml-2 h-4 w-4" />;
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <>
      <AdminNavigation currentPage="Influencer Management" showBackButton={true} />
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Influencer Management</h1>
          <p className="text-muted-foreground">
            Review applications and manage influencer partnerships
          </p>
        </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Applications</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-pending-count">
              {applications?.filter(a => a.status === 'pending').length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Influencers</CardTitle>
            <Users className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-active-influencers">
              {influencers?.filter(i => i.influencerData?.isApproved).length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Currently active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-revenue">
              ${influencers?.reduce((sum, i) => sum + (i.influencerData?.totalEarnings || 0) * 10, 0).toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Generated through referrals</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-conversions">
              {influencers?.reduce((sum, i) => sum + (i.influencerData?.totalReferrals || 0), 0) || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="select-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setDateRange({ from: "", to: "" });
                setFollowerRange({ min: "", max: "" });
              }}
            >
              Clear Filters
            </Button>
          </div>
          <div className="grid md:grid-cols-4 gap-4 mt-4">
            <Input
              type="date"
              placeholder="From Date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              data-testid="input-date-from"
            />
            <Input
              type="date"
              placeholder="To Date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              data-testid="input-date-to"
            />
            <Input
              type="number"
              placeholder="Min Followers"
              value={followerRange.min}
              onChange={(e) => setFollowerRange(prev => ({ ...prev, min: e.target.value }))}
              data-testid="input-min-followers"
            />
            <Input
              type="number"
              placeholder="Max Followers"
              value={followerRange.max}
              onChange={(e) => setFollowerRange(prev => ({ ...prev, max: e.target.value }))}
              data-testid="input-max-followers"
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({applications?.filter(a => a.status === 'pending').length || 0})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">
            Approved ({influencers?.filter(i => i.influencerData?.isApproved).length || 0})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            Rejected ({applications?.filter(a => a.status === 'rejected').length || 0})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            All Applications ({applications?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Pending Applications Tab */}
        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Applications</CardTitle>
              <CardDescription>Review and approve influencer applications</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingApplications ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : paginatedApplications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending applications
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer" onClick={() => handleSort('fullName')}>
                          Name {renderSortIcon('fullName')}
                        </TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="cursor-pointer" onClick={() => handleSort('createdAt')}>
                          Apply Date {renderSortIcon('createdAt')}
                        </TableHead>
                        <TableHead className="cursor-pointer" onClick={() => handleSort('followers')}>
                          Total Followers {renderSortIcon('followers')}
                        </TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedApplications.map((app) => (
                        <TableRow key={app.id} data-testid={`row-application-${app.id}`}>
                          <TableCell className="font-medium">{app.fullName}</TableCell>
                          <TableCell>{app.email}</TableCell>
                          <TableCell>{format(new Date(app.createdAt), 'MMM dd, yyyy')}</TableCell>
                          <TableCell>{formatFollowers(calculateTotalFollowers(app.socialMedia))}</TableCell>
                          <TableCell>{app.location.city}, {app.location.stateOrCountry}</TableCell>
                          <TableCell>
                            <Badge variant={app.status === 'pending' ? 'secondary' : app.status === 'approved' ? 'default' : 'destructive'}>
                              {app.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setSelectedApplication(app)} data-testid={`button-view-${app.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {app.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => {
                                      setSelectedApplication(app);
                                      setActionInfluencer({ id: app.id, action: 'approve', commissionRate: 10 });
                                    }}
                                    data-testid={`button-approve-${app.id}`}
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      setSelectedApplication(app);
                                      setActionInfluencer({ id: app.id, action: 'reject' });
                                    }}
                                    data-testid={`button-reject-${app.id}`}
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          {[...Array(totalPages)].map((_, i) => (
                            <PaginationItem key={i}>
                              <PaginationLink
                                onClick={() => setCurrentPage(i + 1)}
                                isActive={currentPage === i + 1}
                                className="cursor-pointer"
                              >
                                {i + 1}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          <PaginationItem>
                            <PaginationNext
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Influencers Tab */}
        <TabsContent value="approved" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Approved Influencers</CardTitle>
              <CardDescription>Manage active influencers and their performance</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingInfluencers ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : !influencers || influencers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No approved influencers yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Conversions</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Stripe Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {influencers.filter(i => i.influencerData?.isApproved).map((influencer) => (
                      <TableRow key={influencer.id} data-testid={`row-influencer-${influencer.id}`}>
                        <TableCell className="font-medium">{influencer.nickname || 'N/A'}</TableCell>
                        <TableCell>{influencer.email}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {influencer.influencerData?.referralCode}
                          </code>
                        </TableCell>
                        <TableCell>{influencer.influencerData?.totalReferrals || 0}</TableCell>
                        <TableCell>${((influencer.influencerData?.totalEarnings || 0) * 10).toFixed(2)}</TableCell>
                        <TableCell>${(influencer.influencerData?.totalEarnings || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{((influencer.influencerData?.commissionRate || 0) * 100).toFixed(0)}%</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newRate = prompt('Enter new commission rate (%)', 
                                  ((influencer.influencerData?.commissionRate || 0.1) * 100).toString());
                                if (newRate && !isNaN(Number(newRate))) {
                                  updateCommissionMutation.mutate({
                                    id: influencer.id,
                                    commissionRate: Number(newRate)
                                  });
                                }
                              }}
                              data-testid={`button-edit-commission-${influencer.id}`}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {!influencer.stripeConnectAccountId ? (
                            <Badge variant="secondary">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              No Account
                            </Badge>
                          ) : influencer.influencerData?.payoutsEnabled ? (
                            <Badge variant="default">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Clock className="w-3 h-3 mr-1" />
                              Setup Required
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInfluencer(influencer);
                                setShowPerformance(true);
                              }}
                              data-testid={`button-performance-${influencer.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                            {(influencer.influencerData?.pendingEarnings || 0) >= 50 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => payoutMutation.mutate(influencer.id)}
                                disabled={payoutMutation.isPending}
                                data-testid={`button-payout-${influencer.id}`}
                              >
                                {payoutMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CreditCard className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Rejected Applications</CardTitle>
              <CardDescription>Applications that have been rejected</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingApplications ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : paginatedApplications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No rejected applications
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Applied</TableHead>
                      <TableHead>Rejected</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApplications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell>{app.fullName}</TableCell>
                        <TableCell>{app.email}</TableCell>
                        <TableCell>{format(new Date(app.createdAt), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{app.reviewedAt ? format(new Date(app.reviewedAt), 'MMM dd, yyyy') : '-'}</TableCell>
                        <TableCell>{app.rejectionReason || '-'}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => setSelectedApplication(app)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Applications Tab */}
        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Applications</CardTitle>
              <CardDescription>Complete history of all influencer applications</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingApplications ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : paginatedApplications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No applications found
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applied</TableHead>
                      <TableHead>Reviewed</TableHead>
                      <TableHead>Followers</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApplications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell>{app.fullName}</TableCell>
                        <TableCell>{app.email}</TableCell>
                        <TableCell>
                          <Badge variant={app.status === 'pending' ? 'secondary' : app.status === 'approved' ? 'default' : 'destructive'}>
                            {app.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{format(new Date(app.createdAt), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{app.reviewedAt ? format(new Date(app.reviewedAt), 'MMM dd, yyyy') : '-'}</TableCell>
                        <TableCell>{formatFollowers(calculateTotalFollowers(app.socialMedia))}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => setSelectedApplication(app)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Application Detail Sheet */}
      <Sheet open={!!selectedApplication && !actionInfluencer} onOpenChange={() => setSelectedApplication(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px]">
          <SheetHeader>
            <SheetTitle>Application Details</SheetTitle>
            <SheetDescription>Review complete application information</SheetDescription>
          </SheetHeader>
          
          {selectedApplication && (
            <ScrollArea className="h-[calc(100vh-200px)] mt-6">
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="font-semibold mb-3">Personal Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span>{selectedApplication.fullName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span>{selectedApplication.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone:</span>
                      <span>{selectedApplication.phoneNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location:</span>
                      <span>{selectedApplication.location.city}, {selectedApplication.location.stateOrCountry}</span>
                    </div>
                  </div>
                </div>
                <Separator />

                {/* Bio */}
                <div>
                  <h3 className="font-semibold mb-3">Bio</h3>
                  <p className="text-sm text-muted-foreground">{selectedApplication.bio}</p>
                </div>
                <Separator />

                {/* Social Media */}
                <div>
                  <h3 className="font-semibold mb-3">Social Media Presence</h3>
                  <div className="space-y-2">
                    {selectedApplication.socialMedia.instagram?.handle && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Instagram className="w-4 h-4" />
                          <span>Instagram</span>
                        </div>
                        <span>{formatFollowers(selectedApplication.socialMedia.instagram.followers)} followers</span>
                      </div>
                    )}
                    {selectedApplication.socialMedia.tiktok?.username && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <SiTiktok className="w-4 h-4" />
                          <span>TikTok</span>
                        </div>
                        <span>{formatFollowers(selectedApplication.socialMedia.tiktok.followers)} followers</span>
                      </div>
                    )}
                    {selectedApplication.socialMedia.youtube?.url && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Youtube className="w-4 h-4" />
                          <span>YouTube</span>
                        </div>
                        <span>{formatFollowers(selectedApplication.socialMedia.youtube.subscribers)} subscribers</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 p-3 bg-muted rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Reach:</span>
                      <span className="text-lg font-bold">
                        {formatFollowers(calculateTotalFollowers(selectedApplication.socialMedia))}
                      </span>
                    </div>
                  </div>
                </div>
                <Separator />

                {/* Admin Notes */}
                <div>
                  <h3 className="font-semibold mb-3">Admin Notes</h3>
                  <Textarea
                    value={adminNotes || selectedApplication.adminNotes || ''}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this application..."
                    rows={4}
                  />
                </div>

                {/* Commission Rate */}
                {selectedApplication.status === 'pending' && (
                  <>
                    <Separator />
                    <div>
                      <Label htmlFor="commission">Commission Rate (%)</Label>
                      <Input
                        id="commission"
                        type="number"
                        value={commissionRate}
                        onChange={(e) => setCommissionRate(Number(e.target.value))}
                        min="0"
                        max="100"
                        className="mt-2"
                      />
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          )}
          
          <SheetFooter className="mt-6">
            {selectedApplication?.status === 'pending' && (
              <>
                <Button
                  variant="default"
                  onClick={() => setActionInfluencer({ 
                    id: selectedApplication.id, 
                    action: 'approve',
                    commissionRate 
                  })}
                >
                  Approve Application
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setActionInfluencer({ 
                    id: selectedApplication.id, 
                    action: 'reject' 
                  })}
                >
                  Reject Application
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Performance Dashboard Dialog */}
      <Dialog open={showPerformance} onOpenChange={setShowPerformance}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Performance Dashboard</DialogTitle>
            <DialogDescription>
              {selectedInfluencer?.nickname || selectedInfluencer?.email} - Detailed performance metrics
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingPerformance ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : performanceData ? (
            <div className="space-y-6">
              {/* Lifetime Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Total Conversions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {selectedInfluencer?.influencerData?.totalReferrals || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Revenue Generated</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${((selectedInfluencer?.influencerData?.totalEarnings || 0) * 10).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Total Commission</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${(selectedInfluencer?.influencerData?.totalEarnings || 0).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Trend Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={performanceData.monthlyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <ChartTooltip />
                      <Legend />
                      <Area type="monotone" dataKey="conversions" stackId="1" stroke="#8884d8" fill="#8884d8" name="Conversions" />
                      <Area type="monotone" dataKey="revenue" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Revenue ($)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Recent Referrals */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Order ID</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Order Amount</TableHead>
                        <TableHead>Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {performanceData.recentReferrals.slice(0, 5).map((referral) => (
                        <TableRow key={referral.id}>
                          <TableCell>{format(new Date(referral.createdAt), 'MMM dd, yyyy')}</TableCell>
                          <TableCell>#{referral.orderId}</TableCell>
                          <TableCell>{referral.customerEmail}</TableCell>
                          <TableCell>${referral.orderAmount.toFixed(2)}</TableCell>
                          <TableCell>${referral.commission.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Media Downloads */}
              <Card>
                <CardHeader>
                  <CardTitle>Brand Asset Downloads</CardTitle>
                </CardHeader>
                <CardContent>
                  {performanceData.mediaDownloads.length === 0 ? (
                    <p className="text-muted-foreground">No downloads yet</p>
                  ) : (
                    <div className="space-y-2">
                      {performanceData.mediaDownloads.slice(0, 5).map((download) => (
                        <div key={download.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{download.filename}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(download.downloadedAt), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <p className="text-center text-muted-foreground">No performance data available</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPerformance(false)}>Close</Button>
            <Button onClick={() => {
              toast({ title: "Coming Soon", description: "Message functionality will be available soon." });
            }}>
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!actionInfluencer} onOpenChange={() => setActionInfluencer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionInfluencer?.action === 'approve' ? 'Approve Application' : 
               actionInfluencer?.action === 'reject' ? 'Reject Application' :
               actionInfluencer?.action === 'suspend' ? 'Suspend Influencer' :
               'Reactivate Influencer'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionInfluencer?.action === 'approve' 
                ? 'This will approve the application and grant influencer access with the specified commission rate.'
                : actionInfluencer?.action === 'reject'
                ? 'This will reject the application. Please provide a reason for rejection.'
                : actionInfluencer?.action === 'suspend'
                ? 'This will temporarily suspend the influencer account.'
                : 'This will reactivate the influencer account.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {actionInfluencer?.action === 'reject' && (
            <div className="my-4">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Please provide a reason for rejection..."
                value={actionInfluencer.reason || ''}
                onChange={(e) => setActionInfluencer(prev => prev ? { ...prev, reason: e.target.value } : null)}
                className="mt-2"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-action">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-action"
              onClick={() => {
                if (actionInfluencer && selectedApplication) {
                  if (actionInfluencer.action === 'approve' || actionInfluencer.action === 'reject') {
                    reviewMutation.mutate({
                      id: selectedApplication.id,
                      approved: actionInfluencer.action === 'approve',
                      commissionRate: actionInfluencer.commissionRate || commissionRate,
                      adminNotes: adminNotes,
                      rejectionReason: actionInfluencer.reason,
                    });
                  }
                }
              }}
              disabled={reviewMutation.isPending}
            >
              {reviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      <AdminFooter />
    </>
  );
}